import {
  AloftPrice,
  ORDER_FEATURES,
  SalesComparisonApproachType,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';

const getBasementBedrooms = (
  improvements: ReturnType<typeof selectImprovementsEntities>[string],
) =>
  improvements && ORDER_FEATURES.basement.hasFeature(improvements)
    ? (improvements.basement?.bedrooms ?? 0)
    : 0;

const calculateBelowGradeBedroomsAdj = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const compImprovements = improvements[compID];
  if (!subjectImprovements || !compImprovements) return undefined;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: getBasementBedrooms(subjectImprovements),
      compUnitAmount: getBasementBedrooms(compImprovements),
    });
  }

  return undefined;
};

export const selectBelowGradeBedroomsAdj = createPriceSelector(
  [
    selectImprovementsEntities,
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'basementBedrooms'),
  ],
  calculateBelowGradeBedroomsAdj,
);

export const selectBelowGradeBedroomsAdjs = createSelector(
  [
    selectImprovementsEntities,
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'basementBedrooms'),
  ],
  (improvements, compSequence, compTypes, salesComparisonApproach, unitRate) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateBelowGradeBedroomsAdj(
        improvements,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);
